import LoanCompanyAutocomplete from "@/components/Forms/LoanCompanyAutocomplete.tsx";
import { addEditLoanSchema } from "@/components/Forms/schema/addEditLoanSchema.ts";
import { useAddLoanToCase } from "@/mutations/useAddLoanToCase.tsx";
import { useFetchParty } from "@/queries/useFetchParty.ts";
import { getErrorMessage } from "@/utils/api.ts";
import { localDateToDayjs } from "@/utils/datetime.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import type { LocalDate } from "@js-joda/core";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2 as Grid,
} from "@mui/material";
import { RhfTextField } from "mui-rhf-integration";
import { RhfDatePicker } from "mui-rhf-integration/date-picker";
import { enqueueSnackbar } from "notistack";
import { type BaseSyntheticEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

const massageLocalDateToDayjsDates = (data: Record<string, unknown>) => {
    return {
        ...data,
        loanDate: data.loanDate ? localDateToDayjs(data.loanDate as LocalDate | string) : null,
        loanPayoffDate: data.loanPayoffDate
            ? localDateToDayjs(data.loanPayoffDate as LocalDate | string)
            : null,
    };
};

export type AddLoanFormValues = z.infer<typeof addEditLoanSchema>;

type Props = {
    caseUUID: string;
    showAddButton?: boolean;
    editPartyId?: string;
    clearEditPartyId?: () => void;
};

const AddEditLoanDialog = ({
    caseUUID,
    editPartyId,
    clearEditPartyId,
    showAddButton = true,
}: Props): ReactNode => {
    const addLoanToCaseMutation = useAddLoanToCase(caseUUID);
    const [open, setOpen] = useState(false);

    const {
        data: { data } = {},
        isError,
        isRefetching,
        isLoading,
    } = useFetchParty({
        partyId: editPartyId ?? null,
        isQueryEnabled: editPartyId !== undefined,
    });

    const form = useForm<AddLoanFormValues>({
        resolver: zodResolver(addEditLoanSchema),
    });

    const handleSubmit = useCallback(
        (values: AddLoanFormValues) => {
            addLoanToCaseMutation.mutate(
                {
                    ...values,
                },
                {
                    onSuccess: () => {
                        enqueueSnackbar("Loan added.", { variant: "success" });
                        if (clearEditPartyId) {
                            clearEditPartyId();
                        }

                        form.reset({});
                        setOpen(false);
                    },
                    onError: (error) => {
                        enqueueSnackbar("Error: Add loan failed.", { variant: "error" });
                        console.info("error", getErrorMessage(error));
                    },
                },
            );
        },
        [addLoanToCaseMutation, caseUUID],
    );

    const onSubmit: SubmitHandler<AddLoanFormValues> = async (
        data: AddLoanFormValues,
        event?: BaseSyntheticEvent,
    ) => {
        event?.stopPropagation();
        handleSubmit(data);
    };

    useEffect(() => {
        if (editPartyId && data) {
            form.reset(massageLocalDateToDayjsDates(data));
            setOpen(true);
        }
    }, [data, editPartyId]);

    return (
        <>
            {showAddButton && (
                <Button
                    variant={"contained"}
                    sx={{
                        ml: 2,
                        minWidth: "120px",
                    }}
                    onClick={() => {
                        form.reset();
                        setOpen(true);
                    }}
                >
                    New Loan
                </Button>
            )}
            <Dialog open={open} maxWidth="sm" fullWidth>
                <FormProvider {...form}>
                    <form>
                        <DialogTitle>New Loan</DialogTitle>
                        <DialogContent dividers>
                            {(isLoading || isRefetching) && <CircularProgress />}
                            {isError && <Alert severity={"error"}>Error loading form data.</Alert>}
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <LoanCompanyAutocomplete form={form} />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"checkAmountMM"}
                                        label={"Loan Amount"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfDatePicker
                                        control={form.control}
                                        name={"loanDate"}
                                        label={"Loan Date"}
                                        views={["year", "month", "day"]}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"loanPayoffAmount"}
                                        label={"Payoff Amount"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <RhfDatePicker
                                        control={form.control}
                                        name={"loanPayoffDate"}
                                        label={"Payoff Date"}
                                        views={["year", "month", "day"]}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"note"}
                                        label={"Note"}
                                        sx={{
                                            width: "100%",
                                        }}
                                        multiline
                                        rows={4}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    if (clearEditPartyId) {
                                        clearEditPartyId();
                                    }

                                    form.reset({});
                                    setOpen(false);
                                }}
                            >
                                Close
                            </Button>
                            <Button variant={"contained"} onClick={form.handleSubmit(onSubmit)}>
                                {editPartyId ? "Edit" : "New"} Loan
                            </Button>
                        </DialogActions>
                    </form>
                </FormProvider>
            </Dialog>
        </>
    );
};

export default AddEditLoanDialog;
